import { inject, Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { CORE_ROUTES } from "@core/constants/routes.constant";
import { AUTHENTICATE_ROUTES } from "@features/authenticate/constants/routes.constant";
import { KeycloakService, KeycloakStoreService } from "@ogs/ngx-keycloak";
import { fromEvent, merge, timer, Subscription, Observable } from "rxjs";
import { distinctUntilChanged, startWith, switchMap, throttleTime } from "rxjs/operators";

export interface IdlePath {
  path: string;
  uid: string;
}
@Injectable({
  providedIn: "root"
})
export class IdleService {
  private ngZone: NgZone = inject(NgZone);

  private _keyCloakService: KeycloakService = inject(KeycloakService);

  private _keyCloakStoreService: KeycloakStoreService = inject(KeycloakStoreService);

  private _router: Router = inject(Router);

  private _idleTimeoutInMinutes: number = 30;

  private _idleSubscription: Subscription | undefined;

  private _isWatching: boolean = false;

  private _idlePathKey: string = "user-idle-path";

  private _events: string[] = [
    "mousemove",
    "keydown",
    "touchstart",
    "mousedown",
    "scroll",
    "wheel"
  ];

  constructor () {
    this._keyCloakService.isAuthorized$()
      .pipe(
        distinctUntilChanged()
      )
      .subscribe({
        next: (isAuthorized: boolean) => {
          if (isAuthorized && !this._isWatching)
            this.startWatching();
          else if (this._isWatching)
            this.lock();
        }
      });
  }


  /**
   * Get the last path the user visited before being logged out due to inactivity.
   * The path will be read from the sessionStorage and compared with the user-id of the jwt
   *
   * @returns the idle path for the user that is currently logged in or undefined if there is no such path
   */
  public getIdlePath (): string | undefined {
    const idlePath: string | undefined = sessionStorage.getItem(this._idlePathKey) ?? undefined;

    if (idlePath) {
      const parsedIdlePath: IdlePath = JSON.parse(idlePath) as IdlePath;

      if (this._keyCloakStoreService.parsedAccessToken?.sub === parsedIdlePath.uid)
        return parsedIdlePath.path;
    }

    return undefined;
  }

  public hasIdlePath (): boolean {
    return Boolean(sessionStorage.getItem(this._idlePathKey) ?? undefined);
  }

  private saveIdlePath (): void {
    if (this._keyCloakStoreService.parsedAccessToken) {
      const idlePath: IdlePath = {
        path: this._router.url,
        uid: this._keyCloakStoreService.parsedAccessToken.sub
      };

      sessionStorage.setItem(this._idlePathKey, JSON.stringify(idlePath));
    }
  }

  public clearIdlePath (): void {
    sessionStorage.removeItem(this._idlePathKey);
  }

  /**
   * Begins monitoring user activity.
   * When no activity is detected for the configured idle timeout (in minutes),
   * locks the application.
   */
  startWatching (): void {
    this.ngZone.runOutsideAngular(() => {
      const userActivityEvents$: Observable<Event> = merge(
        ...this._events.map((eventItem: string): Observable<Event> => fromEvent(document, eventItem))
      );
      const idleTimer$: Observable<number> = userActivityEvents$.pipe(
        startWith(null),
        throttleTime(1000),
        // Multiplying by 60_000 because "due" parameter of timer expects milliseconds and idleTimeout is in minutes
        switchMap(() => timer(this._idleTimeoutInMinutes * 60_000))
      );

      this._idleSubscription = idleTimer$.subscribe(() => {
        this.ngZone.runOutsideAngular(() => this.lock());
      });
    });
  }


  /**
   * Stops activity monitoring, logs the user out, and redirects to the login page.
   */
  private lock (): void {
    this.saveIdlePath();
    this._keyCloakService.logout().subscribe({
      next: (): void => {
        this.stopWatching();
        void this._router.navigate(
          [ CORE_ROUTES.AUTHENTICATE, AUTHENTICATE_ROUTES.LOGIN ]
        );
      }
    });
  }

  /**
   * Cancels the idle timer subscription (if any) and marks monitoring as stopped.
   */
  stopWatching (): void {
    if (this._idleSubscription) {
      this._idleSubscription.unsubscribe();
      this._idleSubscription = undefined;
    }

    this._isWatching = false;
  }
}
